import {Injectable, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, tap} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthTokensInterface} from '../interfaces/global';
import {jwtDecode} from 'jwt-decode';
import {CookieService} from 'ngx-cookie-service';
import {CallPassword, CallPasswordStatus} from "../interfaces/auth";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  callPassword = signal<CallPassword | null>(null);
  is_recovery = signal<boolean>(false);

  constructor(
    private $http: HttpClient,
    private $router: Router,
    private route: ActivatedRoute,
    private $cookies: CookieService,
  ) {
  }

  public getToken() {
    return this.$cookies.get('qg_token');
  }

  public getRefreshToken() {
    return this.$cookies.get('qg_refresh_token') || '';
  }

  public setTempToken(temp_token: string) {
    localStorage.setItem('temp_token', temp_token);
  }

  public getTempToken() {
    return localStorage.getItem('temp_token');
  }

  public isAuth() {
    return !!this.getToken();
  }

  public logOut() {
    const hostname = window.location.hostname;
    const mainDomain = hostname.split('.').slice(-2).join('.');
    this.$cookies.delete('qg_token', '/', mainDomain);
    this.$cookies.delete('qg_refresh_token', '/', mainDomain);
    this.$router
      .navigate(['/login'], {
        queryParams: {
          ...this.route.snapshot.queryParams
        },
        fragment: this.route.snapshot.fragment || undefined,
        relativeTo: this.route
      }).then();
  }

  /**
   *
   * @param access_token
   * @param refresh_token
   */
  public setToken(access_token: string, refresh_token: string) {
    const hostname = window.location.hostname;
    const mainDomain = hostname.split('.').slice(-2).join('.');
    this.$cookies.set('qg_token', access_token, {
      domain: '.' + mainDomain
    });
    this.$cookies.set('qg_refresh_token', refresh_token, {
      domain: '.' + mainDomain,
      expires: new Date(jwtDecode(refresh_token).exp! * 1000)
    });
  }

  /**
   * метод для получения кода при авторизации
   * type (false - почта, true - телефон)
   * value (введенное значение)
   * country_id (выбранная страна)
   */
  public getCodeAuth(
    type: boolean,
    value: string | null,
    country_id: number
  ): Observable<{ temporary_token: string }> {
    const language_tag = localStorage.getItem('lang') || 'ru';
    const body = {
      [type ? 'phone' : 'email']: value,
      language_tag,
      country_id
    };

    return this.$http.post<{ temporary_token: string }>('auth', body, {
      params: {
        isSSO: true,
        hideToast: true
      }
    });
  }

  /**
   * подтверждение авторизации по коду
   * type (false - почта, true - телефон)
   * short_session - не запоминать на этом устройстве
   */
  public verifyCode(
    type: 0 | 1,
    value: string | null,
    verify_code: string,
    short_session: boolean
  ): Observable<AuthTokensInterface> {
    const body = {
      [type ? 'phone' : 'email']: value,
      verify_code,
      short_session
    };

    return this.$http.post<AuthTokensInterface>('auth/verify', body, {
      headers: {
        Authorization: `Bearer ${this.getTempToken()}`
      },
      params: {
        isSSO: true
      }
    });
  }

  public resetVerifyCode(
    type: 0 | 1,
    value: string | null,
    verify_code: string
  ): Observable<{ temporary_token: string }> {
    const body = {
      [type ? 'phone' : 'email']: value,
      verify_code
    };

    return this.$http.post<{ temporary_token: string }>(
      'password-reset/verify',
      body,
      {
        headers: {
          Authorization: `Bearer ${this.getTempToken()}`
        },
        params: {
          isSSO: true
        }
      }
    );
  }

  /**
   * метод для сброса пароля
   * @param value - email или телефон, для подтверждения смены пароля
   */
  public resetPassword(value: string): Observable<{ temporary_token: string }> {
    const language_tag = localStorage.getItem('lang');
    const body = {...this.validLogin(value), language_tag};

    return this.$http.post<{ temporary_token: string }>(
      'password-reset/request',
      body,
      {
        params: {
          isSSO: true
        }
      }
    );
  }

  /**
   * Вход по логину и паролю
   * @param login
   * @param password
   * @param short_session - не запоминать на этом устройстве
   */
  public loginWithPassword(
    login: string,
    password: string,
    short_session: boolean
  ): Observable<AuthTokensInterface> {
    const body = {
      ...this.validLogin(login),
      password,
      short_session
    };
    return this.$http.post<AuthTokensInterface>('auth/password', body, {
      params: {
        isSSO: true
      }
    });
  }

  public passwordResetComplete(
    password: string,
    short_session: boolean
  ): Observable<AuthTokensInterface> {
    const body = {
      password,
      password_confirm: password,
      short_session
    };
    return this.$http.post<AuthTokensInterface>(
      'password-reset/complete',
      body,
      {
        headers: {
          Authorization: `Bearer ${this.getTempToken()}`
        },
        params: {
          isSSO: true
        }
      }
    );
  }

  public updateToken(): Observable<AuthTokensInterface> {
    return this.$http.post<AuthTokensInterface>(
      'auth/refresh-access-token',
      null,
      {
        headers: {
          Authorization: `Bearer ${this.getRefreshToken()}`
        },
        params: {
          isSSO: true
        }
      }
    );
  }

  public validLogin(login: string) {
    const regex = /[a-zA-Z]/;
    return login.includes('@') || regex.test(login)
      ? {email: login}
      : {phone: this.setPhoneNumber(login)};
  }

  private setPhoneNumber(phone: string) {
    if (!phone.startsWith('+')) return '+' + phone;
    return phone;
  }

  isLive() {
    const exp_token = jwtDecode(this.getToken()!).exp || 0;
    return Date.now() <= exp_token * 1000;
  }

  // запрос на получение номера для авторизации по newTel
  public callPasswordID(phone: string, country_id: number, language_tag: string) {
    const body = {
      phone,
      country_id,
      language_tag
    }

    return this.$http.post<CallPassword>('auth/phone/call-password-id', body, {
      params: {
        isSSO: true
      }
    })
      .pipe(
        tap((res) => {
          this.setTempToken(res.temporary_token)
        })
      )
  }

  public checkCallPasswordStatus() {
    return this.$http.get<{ status: CallPasswordStatus }>('auth/phone/call-password-id/status', {
      params: {
        isSSO: true
      },
      headers: {
        Authorization: `Bearer ${this.getTempToken()}`
      }
    })
  }

  public callPasswordVerify() {
    const body = {
      short_session: false
    }

    return this.$http.post<AuthTokensInterface>('auth/phone/call-password-id/verify', body, {
      headers: {
        Authorization: `Bearer ${this.getTempToken()}`
      },
      params: {
        isSSO: true
      }
    })
      .pipe(
        tap((res) => {
          this.setToken(res.access_token, res.refresh_token);
        })
      )
  }

  public callResetPasswordID(phone: string, country_id: number, language_tag: string) {
    const body = {
      phone,
      country_id,
      language_tag
    }

    return this.$http.post<CallPassword>('password-reset/phone/call-password-id', body, {
      params: {
        isSSO: true
      }
    })
      .pipe(
        tap((res) => {
          this.setTempToken(res.temporary_token)
        })
      )
  }

  public checkCallResetPasswordStatus() {
    return this.$http.get<{ status: CallPasswordStatus }>('password-reset/phone/call-password-id/status', {
      params: {
        isSSO: true
      },
      headers: {
        Authorization: `Bearer ${this.getTempToken()}`
      }
    })
  }

  public checkCallUpdatePhoneStatus() {
    return this.$http.get<{ status: CallPasswordStatus }>('auth/phone/change/call-password-id/status', {
      params: {
        isSSO: true,
        loader: false,
      },
      headers: {
        Authorization: `Bearer ${this.getTempToken()}`
      }
    })
  }

  public callResetPasswordVerify() {
    const body = {};

    return this.$http.post<{ temporary_token: string }>('password-reset/phone/call-password-id/verify', body, {
      headers: {
        Authorization: `Bearer ${this.getTempToken()}`
      },
      params: {
        isSSO: true
      }
    })
      .pipe(
        tap((res) => {
          this.setTempToken(res.temporary_token);
        })
      )
  }

  public callUpdatePhoneVerify() {
    const body = {};

    return this.$http.post('auth/phone/change/call-password-id/verify', body, {
      headers: {
        Authorization: `Bearer ${this.getTempToken()}`
      },
      params: {
        isSSO: true
      }
    })
  }
}
